/**
 * Bucketing events into the day they fall on, in the order they happen.
 *
 * Every calendar view asks the same question — what is on this day, earliest
 * first — so the grouping lives here rather than being rewritten, and slightly
 * differently, in each grid.
 */

/** The part of an event this module reads. */
export interface DatedItem {
  /** The civil date the event falls on, "YYYY-MM-DD". */
  date: string;
  /** 24-hour start time, "HH:MM". */
  startTime: string;
  /** Used only to break ties between events starting at the same minute. */
  title?: string;
}

/**
 * Events keyed by their date, each day's list ordered by start time.
 *
 * The order matters more than it looks: the month cell names only its first
 * few events before it starts counting, so an unsorted day would name three
 * arbitrary events and hide the ones the reader most likely wants — the
 * evening's first.
 *
 * Ties are broken by title so a day's order is stable between renders rather
 * than depending on the order the query happened to return.
 */
export function groupEventsByDate<T extends DatedItem>(events: T[]): Map<string, T[]> {
  const byDate = new Map<string, T[]>();

  for (const event of events) {
    const existing = byDate.get(event.date);
    if (existing) existing.push(event);
    else byDate.set(event.date, [event]);
  }

  for (const dayEvents of byDate.values()) {
    dayEvents.sort(
      (a, b) =>
        a.startTime.localeCompare(b.startTime) || (a.title ?? "").localeCompare(b.title ?? "")
    );
  }

  return byDate;
}
